/**
 * Fiche publique d'un cours, et lecture d'une leçon (docs/11 §6).
 *
 * Ce que l'écran affiche vient **entièrement** du serveur : les leçons en
 * brouillon ne sont pas dans la réponse, et `is_readable` dit si le contenu
 * s'ouvre. L'écran ne refait aucune de ces règles — le jour où l'inscription
 * entrera dans le calcul (phase 5), il n'aura rien à changer.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCourseBySlug, useLesson } from "@/features/catalog/api/useCatalog";
import { RichContent } from "@/shared/richtext/RichContent";
import { ResourceList } from "@/features/catalog/components/ResourceList";
import { StatusBadge } from "@/features/catalog/components/StatusBadge";
import { asDocument } from "@/shared/richtext/document";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function CoursePage() {
  const { t } = useTranslation("catalog");
  const { slug } = useParams({ from: "/public/courses/$slug" });
  const course = useCourseBySlug(slug);
  const [openLessonId, setOpenLessonId] = useState<string | undefined>(undefined);
  const lesson = useLesson(course.data?.course.id ?? "", openLessonId);

  if (course.isPending) {
    return (
      <Skeleton className="mx-auto my-8 h-64 w-full max-w-4xl" label={t("course.curriculum")} />
    );
  }
  if (course.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={course.error} />
      </div>
    );
  }

  const detail = course.data;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">{detail.course.title}</h1>
          {/* Le statut n'est montré qu'au propriétaire : un visiteur ne voit
              que des cours publiés, et une pastille « publié » sur tout le
              catalogue n'apprendrait rien. */}
          {detail.can_edit && <StatusBadge status={detail.course.status} />}
        </div>
        {/* L'entrée du parcours élève. Proposée sur tout cours publié, sans
            vérifier au préalable si l'appelant est inscrit : c'est le player
            qui le sait, et lui qui propose l'inscription. Interroger ici
            ajouterait une requête à la page la plus consultée du produit pour
            changer un libellé. */}
        {detail.course.status === "published" && (
          <Link
            to="/learn/$slug"
            params={{ slug: detail.course.slug }}
            className="text-brand-600 text-sm font-medium hover:underline"
          >
            {t("course.follow")}
          </Link>
        )}
        {detail.course.subtitle !== null && (
          <p className="text-slate-600 dark:text-slate-400">{detail.course.subtitle}</p>
        )}
      </header>

      {detail.description !== null && (
        <Card title={t("course.about")}>
          <div className="text-sm text-slate-700 dark:text-slate-300">
            <RichContent document={asDocument(detail.description)} />
          </div>
        </Card>
      )}

      <Card title={t("course.curriculum")}>
        {detail.lessons.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("course.noLesson")}</p>
        ) : (
          <ol className="space-y-2">
            {detail.lessons.map((item) => (
              <li
                key={item.id}
                className="rounded-md border border-slate-200 dark:border-slate-800"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left disabled:cursor-not-allowed"
                  disabled={!item.is_readable}
                  aria-expanded={openLessonId === item.id}
                  onClick={() => {
                    setOpenLessonId(openLessonId === item.id ? undefined : item.id);
                  }}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 dark:text-slate-400">{item.position}.</span>
                    <span>{item.title}</span>
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {item.is_readable ? t("course.read") : t("course.locked")}
                  </span>
                </button>

                {openLessonId === item.id && (
                  <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-800">
                    {lesson.isPending && (
                      <Skeleton className="h-24 w-full" label={t("course.read")} />
                    )}
                    {lesson.isError && <ErrorState error={lesson.error} />}
                    {lesson.data !== undefined && (
                      <>
                        <RichContent document={asDocument(lesson.data.content)} />
                        <ResourceList resources={lesson.data.resources} />
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
